import { handleAPIError } from "handlers/setters";
import { generateQueryString } from "utils/query";
import { instanceWithRetry, instanceMultipart } from "actions/axiosInstance";
import { downloadFile } from "utils";

export const uploadRemittance = (e) => {
  const bodyFormData = new FormData();
  const fileList = Object.values(e.target.files);
  fileList.forEach((file) => bodyFormData.append("file", file, file.name));
  return instanceMultipart
    .post(`/cash_app/uploadfile?mutate=1`, bodyFormData)
    .catch((e) => handleAPIError(e, true));
};

export const deleteRemittance = (id) =>
  instanceWithRetry
    .delete(`/cash_app/file/delete/${id}?mutate=1`)
    .catch((e) => handleAPIError(e));

export const getFileList = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/files${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getFileDetails = (id) =>
  instanceWithRetry
    .get(`/cash_app/file/details/${id}`)
    .catch((e) => handleAPIError(e));

export const getPaymentDetails = (id, params = { mutate: 1 }) =>
  instanceWithRetry
    .get(`/cash_app/invoices/${id}${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getInvoicePaymentDetail = (id, lineId, params = { mutate: 1 }) =>
  instanceWithRetry
    .get(`/cash_app/user_selections/${id}${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const applyChargeback = (id, user_selection_id, params = {}) =>
  instanceWithRetry
    .post(
      `/cash_app/user_selections/chargeback/${id}/${user_selection_id}?mutate=1`,
      params
    )
    .catch((e) => handleAPIError(e));

export const deleteChargeback = (id, user_selection_id, params = {}) =>
  instanceWithRetry
    .delete(
      `/cash_app/user_selections/chargeback/${id}/${user_selection_id}?mutate=1`,
      params
    )
    .catch((e) => handleAPIError(e));

export const updatePayment = (id, user_selection_id, params = {}) =>
  instanceWithRetry
    .put(
      `/cash_app/user_selections/${id}/${user_selection_id}?mutate=1`,
      params
    )
    .catch((e) => handleAPIError(e));

export const applyPayment = (id, params = {}) =>
  instanceWithRetry
    .put(`/cash_app/user_selections/apply/${id}?mutate=1`, params)
    .catch((e) => handleAPIError(e));

export const getSyncRequest = (id, params = {}) =>
  instanceWithRetry
    .get(`/cash_app/sync_requests/${id}${generateQueryString(params)}`)
    .then((response) => {
      // Check if the response content-type is text/csv
      const contentType = response.headers["content-type"];
      if (contentType && contentType.includes("text/csv")) {
        // Download the file and return null for chained calls
        downloadFile({
          data: response.data,
          fileName: `matching-${id}-${new Date().getTime()}.csv`,
        });
        return null;
      }
      // Default case: return the response for further processing
      return response;
    })
    .catch((e) => handleAPIError(e));

export const updateSyncRequests = (id, params = {}) =>
  instanceWithRetry
    .put(`/cash_app/sync_requests/${id}?mutate=1`, params)
    .catch((e) => handleAPIError(e));

export const addSyncRequest = (id, params = {}) =>
  instanceWithRetry
    .put(`/cash_app/sync_requests/add/${id}${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const updateSyncRequest = (id, erpId, params = {}) =>
  instanceWithRetry
    .post(`/cash_app/sync_requests/${id}/${erpId}?mutate=1`, params)
    .catch((e) => handleAPIError(e));

export const postSyncRequest = (id, erpId, params = {}) =>
  instanceWithRetry
    .post(`/cash_app/sync_requests/sync/${id}/${erpId}?mutate=1`, params)
    .catch((e) => handleAPIError(e));

export const getDeductionCode = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/deduction_codes${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getCheckingAccounts = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/checking_accounts${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getBankAccounts = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/bank_accounts${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getSageIntacctPaymentMethods = () =>
  instanceWithRetry
    .get(`/v1.0/sage_intacct/payment_methods`)
    .catch((e) => handleAPIError(e));

export const getNetsuitePaymentMethods = () =>
  instanceWithRetry
    .get(`paymentmethod?mutate=1`)
    .catch((e) => handleAPIError(e));

export const getNetsuitePaymentOptions = () =>
  instanceWithRetry
    .get(`paymentoption?mutate=1`)
    .catch((e) => handleAPIError(e));

export const getSyncEditableFields = (id) =>
  instanceWithRetry
    .get(`/cash_app/sync_requests/editable/${id}`)
    .catch((e) => handleAPIError(e));

export const getFileFormats = () =>
  instanceWithRetry
    .get(`/cash_app/file_formats`)
    .catch((e) => handleAPIError(e));

export const downloadReportFile = (id, name) => {
  instanceWithRetry
    .get(`/cash_app/sync_requests/reports/basic/${id}`)
    .then((response) => {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.download = `${name}-basic-erp-report.csv`;
      link.click();
    })
    .catch((e) => handleAPIError(e));
};

export const downloadCombinedReportFile = () => {
  instanceWithRetry
    .get(`/cash_app/sync_requests/reports/basic/combined`)
    .then((response) => {
      downloadFile({
        data: response.data,
        fileName: `combined-erp-report${new Date().getTime()}.csv`,
      });
    })
    .catch((e) => handleAPIError(e));
};

export const downloadPostCombinedReportFile = (filter) => {
  instanceWithRetry
    .post(`/cash_app/sync_requests/reports/basic/combined`, filter)
    .then((response) => {
      downloadFile({
        data: response.data,
        fileName: `combined-erp-report${new Date().getTime()}.csv`,
      });
    })
    .catch((e) => handleAPIError(e));
};

export const downloadOriginalFile = (id, name, original = false) => {
  let url = `/cash_app/file/download/${id}`;
  if (original) {
    url = `/cash_app/file/download/${id}/true`;
  }

  // Configure axios to handle binary data properly when original=true
  const config = original ? { responseType: "arraybuffer" } : {};

  instanceWithRetry
    .get(url, config)
    .then((response) => {
      let file_content = response.data;

      if (original) {
        if (response.data instanceof ArrayBuffer) {
          file_content = response.data;
        } else if (response.data instanceof Uint8Array) {
          file_content = response.data;
        } else if (typeof response.data === "string") {
          const bytes = new Uint8Array(response.data.length);
          for (let i = 0; i < response.data.length; i++) {
            bytes[i] = response.data.charCodeAt(i);
          }
          file_content = bytes.buffer;
        }
      }

      downloadFile({
        data: file_content,
        fileName: name,
        fileType: response.headers["content-type"],
      });
    })
    .catch((e) => handleAPIError(e));
};

// Stats API functions
export const getRemittanceStats = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/file/stats${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getBankFileStats = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/stats${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getRunningNumbers = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/running-numbers${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const getLatestBankFile = () =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/latest`)
    .catch((e) => handleAPIError(e));

export const downloadBankFileExport = (filter, callback) => {
  instanceWithRetry
    .post(`/cash_app/bankfiles/export-output`, filter)
    .then((response) => {
      downloadFile({
        data: response.data.csv_data,
        fileName: `bank-files-export-${new Date().getTime()}.csv`,
      });
      if (callback) callback();
    })
    .catch((e) => handleAPIError(e));
};

export const fetchFTPConfigForRemittance = (params = {}) =>
  instanceWithRetry
    .get(`/cash_app/ftp_configs${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const updateFTPConfigForRemittance = (id, data) =>
  id
    ? instanceWithRetry
        .put(`/cash_app/ftp_configs/${id}?mutate=1`, data)
        .catch((e) => handleAPIError(e))
    : instanceWithRetry
        .post(`/cash_app/ftp_configs?mutate=1`, data)
        .catch((e) => handleAPIError(e));

export const deleteFTPConfigForRemittance = (id) =>
  instanceWithRetry
    .delete(`/cash_app/ftp_configs/${id}?mutate=1`)
    .catch((e) => handleAPIError(e));

export const triggerFTPFetchForRemittance = (id) =>
  instanceWithRetry
    .post(`/cash_app/ftp_configs/${id}/fetch?mutate=1`)
    .catch((e) => handleAPIError(e));

export const getRemittanceFileCustomers = (remittanceFileId) =>
  instanceWithRetry
    .get(`/cash_app/customers/${remittanceFileId}`)
    .catch((e) => handleAPIError(e));

export const createOnAccountPosting = (remittanceFileId, data) =>
  instanceWithRetry
    .post(
      `/cash_app/user_selections/${remittanceFileId}/create_on_account_posting?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e));

export const consolidateUserSelection = (remittanceFileId, userSelectionId) =>
  instanceWithRetry
    .post(
      `/cash_app/user_selections/${remittanceFileId}/${userSelectionId}/consolidate?mutate=1`
    )
    .catch((e) => handleAPIError(e));

export const updateARInvoiceDetails = (remittanceFileId, arInvoiceId, data) =>
  instanceWithRetry
    .put(
      `/cash_app/user_selections/ar_invoice/${remittanceFileId}/${arInvoiceId}?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e));

export const resetARInvoiceSelection = (remittanceFileId, arInvoiceId) =>
  instanceWithRetry
    .put(
      `/cash_app/user_selections/ar_invoice/${remittanceFileId}/${arInvoiceId}/reset?mutate=1`
    )
    .catch((e) => handleAPIError(e));

export const updateRemittanceFilePaymentDate = (remittanceFileId, data) =>
  instanceWithRetry
    .put(
      `/cash_app/file/details/${remittanceFileId}/payment_date?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e, true));
export const overrideLinkageWithFile = (
  remittanceFileId,
  remittanceBankFileLineId,
  data
) =>
  instanceWithRetry
    .post(
      `/cash_app/file/${remittanceFileId}/override_linkage_with_file/${remittanceBankFileLineId}?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e));

export const searchARInvoices = (params = {}) =>
  instanceWithRetry
    .get(`/arinvoice/search${generateQueryString(params)}`)
    .catch((e) => handleAPIError(e));

export const allocateInvoiceToARInvoice = (
  remittanceFileId,
  remittanceFileArInvoiceId,
  data
) =>
  instanceWithRetry
    .post(
      `/cash_app/invoices/${remittanceFileId}/${remittanceFileArInvoiceId}/allocate?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e));
